import AppIcon from "common/app-icon";
import { ConfirmModalStyle } from "common/components/confirm-modal";
import { formatLocalizedString, getLocalizedString } from "common/components/localization";
import { isEmptyOrUndefined, nonNull } from "common/Support";
import * as React from "react";
import { createPortal } from "react-dom";
import { showCancelUploadModal } from "webapp/lib/in-app-modals";
import Img from "webapp/ui/smart/img/img";
import ConfirmModal from "../../../chrome/views/confirm-modal/confirm-modal";
import { APP_MODAL_ROOT } from "../../../lib/constants";
import { BasePicker } from "../base-picker/base-picker";
import { FormElementProps } from "../form-element/form-element";

export type FilePickerMode = "normal" | "uploading" | "upload-failed" | "deleting";

export interface FilePickerViewProps extends FormElementProps {
    readonly value?: string;
    readonly valueLabel?: string;
    readonly isRequired?: boolean;
    readonly iconSource?: string;
    readonly showPreview?: boolean;
    readonly deleteModalTitle?: string;
    readonly deleteModalDescription?: string;

    readonly mode?: FilePickerMode;

    readonly capture?: string;
    readonly idOverride?: string;
    readonly mimePattern?: string;
    readonly className?: string;

    readonly onClearClicked?: (ev: React.MouseEvent<HTMLDivElement>) => void;
    readonly onInputChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
    readonly onDelete?: (shouldDelete: boolean) => void;
    readonly onCancel?: (shouldCancel: boolean) => void;
}

interface Props extends FilePickerViewProps {
    readonly progressPercentage?: number;
}

let count = 1;

class FilePickerViewImpl extends React.PureComponent<Props> {
    private inputRef = React.createRef<HTMLInputElement>();

    private ct: number = count++;

    private typeIcon(): JSX.Element {
        const { iconSource = "16-02-folder-file-1" } = this.props;
        return (
            <div className={"preview-wrapper"}>
                <AppIcon icon={iconSource} size={22} />
            </div>
        );
    }

    private onCancelClicked = async (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault();
        ev.stopPropagation();

        const { onCancel } = this.props;
        onCancel?.(await showCancelUploadModal());
    };

    public componentWillUnmount() {
        // Cancel all pending uploads when the component unmounts.
        // Clearly, we don't care about them anymore.
        // FIXME: We should probably not be relying on React state to
        // do this...
        const { mode, onCancel } = this.props;
        if (mode !== "uploading") return;

        onCancel?.(true);
    }

    public componentDidUpdate(prevProps: Props) {
        const { current } = this.inputRef;
        if (current === null) return;
        if (prevProps.mode === "uploading" && this.props.mode !== "uploading") {
            // If we don't do this on a mode change, you can't upload the same file again.
            current.value = "";
        }
    }

    public render(): React.ReactNode {
        const {
            value = undefined,
            title,
            mode = "normal",
            idOverride,
            mimePattern,
            showPreview = false,
            deleteModalTitle = "Delete File",
            deleteModalDescription = "Are you sure you want to delete this file?",
            className,
            isEnabled = true,
            capture,
            progressPercentage,
            onDelete,
            onClearClicked,
            onInputChange,
        } = this.props;

        const id = "item-upload-" + (idOverride ?? this.ct);

        let portal: React.ReactNode;
        if (mode === "deleting") {
            portal = createPortal(
                <ConfirmModal
                    title={deleteModalTitle}
                    description={deleteModalDescription}
                    accept={getLocalizedString("delete")}
                    cancel={getLocalizedString("cancel")}
                    onFinish={d => onDelete?.(d)}
                    modalStyle={ConfirmModalStyle.Delete}
                />,
                nonNull(document.getElementById(APP_MODAL_ROOT))
            );
        }

        const isUploading = mode === "uploading";
        const hasValue = !isEmptyOrUndefined(value);
        const isPlaceholder = mode !== "normal" || !hasValue;

        let { valueLabel } = this.props;
        if (isUploading) {
            valueLabel = formatLocalizedString("uploadProgress", [Math.floor(progressPercentage ?? 0).toString(10)]);
        }

        let errorMessage: string | undefined;
        if (mode === "upload-failed") {
            errorMessage = getLocalizedString("uploadFailedInline");
        }

        return (
            <BasePicker
                {...this.props}
                className={className}
                data-test="app-file-picker"
                progressPercentage={isUploading ? progressPercentage : undefined}
                isPlaceholder={isPlaceholder}
                leftItem={
                    hasValue && showPreview ? (
                        <Img className={(isUploading ? "fp-uploading" : undefined) + ` image`} src={value} />
                    ) : (
                        this.typeIcon()
                    )
                }
                errorMessage={errorMessage}
                rightItem={!isEmptyOrUndefined(value) || isUploading ? "clear" : undefined}
                rightItemOnClick={isUploading ? this.onCancelClicked : hasValue ? onClearClicked : undefined}>
                <label htmlFor={id}>{valueLabel}</label>
                {portal}
                <input
                    ref={this.inputRef}
                    capture={capture}
                    type="file"
                    id={id}
                    accept={mimePattern}
                    onChange={onInputChange}
                    disabled={!isEnabled || mode === "uploading"}
                    aria-label={`${title} file browser`}
                />
            </BasePicker>
        );
    }
}

const FilePickerView = FilePickerViewImpl;
export default FilePickerView;
